"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  MessageCircle,
  KanbanSquare,
  Hash,
  Pin,
  Paperclip,
  AtSign,
  CircleCheck,
  Calendar,
  Eye,
  MapPin,
  Clock,
  Users,
  Zap,
} from "lucide-react";
import { fadeUp, scaleIn, Section, agents } from "./utils";

type ThreadMessage = {
  agent: (typeof agents)[number];
  time: string;
  content: React.ReactNode;
  reactions?: { emoji: string; count: number }[];
  isSystem?: boolean;
  systemIcon?: React.ElementType;
  systemColor?: string;
  delay: number;
  typingDuration: number;
};

function TypingIndicator({ agent }: { agent: (typeof agents)[number] }) {
  return (
    <div className="flex gap-3 py-2.5 px-2">
      <img
        src={agent.avatar}
        alt={agent.name}
        className="w-9 h-9 rounded-lg object-cover shrink-0 mt-0.5"
      />
      <div className="flex items-center gap-1.5 bg-surface-tertiary/60 rounded-2xl px-4 py-3">
        <motion.div
          className="w-2 h-2 rounded-full bg-content-muted"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: 0 }}
        />
        <motion.div
          className="w-2 h-2 rounded-full bg-content-muted"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }}
        />
        <motion.div
          className="w-2 h-2 rounded-full bg-content-muted"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: 0.4 }}
        />
      </div>
    </div>
  );
}

function AnimatedNumber({
  target,
  duration = 1.5,
  suffix = "",
}: {
  target: number;
  duration?: number;
  suffix?: string;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const start = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target, duration]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}

export function AgentCollaboration() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [typingAgent, setTypingAgent] = useState<(typeof agents)[number] | null>(null);
  const [pipelineStage, setPipelineStage] = useState(0);
  const [dealValue, setDealValue] = useState("—");
  const [responseTime, setResponseTime] = useState("—");
  const threadRef = useRef<HTMLDivElement>(null);

  const messages: ThreadMessage[] = [
    {
      agent: agents[0],
      time: "10:47 PM",
      delay: 0.5,
      typingDuration: 1.2,
      content: (
        <>
          New inbound — Ahmed is asking about the 2BR in Marina
          Tower. Budget: AED 1.8M, timeline: &ldquo;as soon as possible.&rdquo;
          <div className="mt-2.5 bg-surface-tertiary/80 rounded-lg p-3 border border-surface-hover/40">
            <div className="flex items-center gap-2 text-[11px] text-content-secondary mb-1.5">
              <MessageCircle size={12} className="text-ok" />
              Inbound Lead · PropNest Listing #8842
            </div>
            <p className="text-[12px] text-content/80">
              &ldquo;Hi, I saw the 2BR listing on PropNest. Is it still available?
              Looking to move in within 2 months.&rdquo;
            </p>
          </div>
        </>
      ),
      reactions: [{ emoji: "👀", count: 2 }],
    },
    {
      agent: agents[0],
      time: "10:47 PM",
      delay: 3.5,
      typingDuration: 1.0,
      content: (
        <>
          Replied to Ahmed in{" "}
          <span className="text-ok font-semibold">8 seconds</span>.
          Offered two viewing slots for tomorrow.{" "}
          <span className="relative inline-block">
            <span className="text-info font-medium">@Omar</span>
            <span className="absolute inset-0 rounded bg-info/20 animate-pulse" />
          </span>{" "}
          — can you check availability and prep the viewing docs?
        </>
      ),
    },
    {
      agent: agents[1],
      time: "10:49 PM",
      delay: 6.0,
      typingDuration: 1.5,
      content: (
        <>
          Unit confirmed available. Viewing docs prepped and signed.
          I&apos;ve locked in two slots:
          <div className="mt-2 flex gap-2 flex-wrap">
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="inline-flex items-center gap-1 text-[10px] bg-ok/10 text-ok border border-ok/20 rounded-full px-2.5 py-1"
            >
              <CircleCheck size={10} /> Docs ready
            </motion.span>
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="inline-flex items-center gap-1 text-[10px] bg-info/10 text-info border border-info/20 rounded-full px-2.5 py-1"
            >
              <Calendar size={10} /> Tomorrow 10 AM
            </motion.span>
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.7, type: "spring" }}
              className="inline-flex items-center gap-1 text-[10px] bg-info/10 text-info border border-info/20 rounded-full px-2.5 py-1"
            >
              <Calendar size={10} /> Tomorrow 2 PM
            </motion.span>
          </div>
        </>
      ),
      reactions: [{ emoji: "✅", count: 1 }],
    },
    {
      isSystem: true,
      systemIcon: KanbanSquare,
      systemColor: "text-info",
      agent: agents[1],
      time: "10:49 PM",
      delay: 8.5,
      typingDuration: 0,
      content: (
        <span className="text-content-muted">
          <span className="text-content/80 font-medium">Omar</span> moved{" "}
          <span className="text-content/80">Ahmed — Marina Tower 2BR</span> from{" "}
          <span className="text-content-secondary bg-surface-tertiary px-1.5 py-0.5 rounded text-[10px]">
            New Lead
          </span>{" "}
          →{" "}
          <motion.span
            initial={{ scale: 0.8 }}
            animate={{ scale: [1.15, 1] }}
            transition={{ duration: 0.3 }}
            className="inline-block text-info bg-info/10 px-1.5 py-0.5 rounded text-[10px] font-medium"
          >
            Viewing Scheduled
          </motion.span>
        </span>
      ),
    },
    {
      agent: agents[0],
      time: "10:50 PM",
      delay: 10.0,
      typingDuration: 1.0,
      content: (
        <>
          Ahmed chose the 10 AM slot. Sent confirmation with{" "}
          <span className="text-content">
            <MapPin size={12} className="inline text-coral-400" /> location pin
          </span>{" "}
          and parking instructions.{" "}
          <span className="relative inline-block">
            <span className="text-info font-medium">@Layla</span>
            <span className="absolute inset-0 rounded bg-info/20 animate-pulse" />
          </span>{" "}
          — flag for follow-up tomorrow 3 PM if viewing goes well.
        </>
      ),
    },
    {
      agent: agents[2],
      time: "10:51 PM",
      delay: 12.5,
      typingDuration: 1.3,
      content: (
        <>
          Follow-up locked in. I&apos;ll check in after the viewing and send a
          satisfaction survey. If interested, I&apos;ll loop{" "}
          <span className="relative inline-block">
            <span className="text-info font-medium">@Omar</span>
            <span className="absolute inset-0 rounded bg-info/20 animate-pulse" />
          </span>{" "}
          back for the offer paperwork.
          <div className="mt-2 flex gap-2 flex-wrap">
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="inline-flex items-center gap-1 text-[10px] bg-coral-500/10 text-coral-400 border border-coral-500/20 rounded-full px-2.5 py-1"
            >
              <Clock size={10} /> Follow-up: Tomorrow 3 PM
            </motion.span>
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="inline-flex items-center gap-1 text-[10px] bg-highlight/10 text-highlight border border-highlight/20 rounded-full px-2.5 py-1"
            >
              <Eye size={10} /> Auto-survey queued
            </motion.span>
          </div>
        </>
      ),
      reactions: [
        { emoji: "🔥", count: 2 },
        { emoji: "👏", count: 1 },
      ],
    },
  ];

  useEffect(() => {
    if (!isInView) return;
    let cancelled = false;

    const runSequence = async () => {
      for (let i = 0; i < messages.length; i++) {
        if (cancelled) return;
        const msg = messages[i];

        await new Promise((r) => setTimeout(r, msg.delay * 1000 - (i > 0 ? messages[i - 1].delay * 1000 : 0)));
        if (cancelled) return;

        if (!msg.isSystem && msg.typingDuration > 0) {
          setTypingAgent(msg.agent);
          await new Promise((r) => setTimeout(r, msg.typingDuration * 1000));
          if (cancelled) return;
          setTypingAgent(null);
        }

        setVisibleMessages(i + 1);

        if (i === 3) setPipelineStage(2);
        if (i === 1) setResponseTime("8s");
        if (i === 2) setDealValue("AED 1.8M");

        if (threadRef.current) {
          threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
      }
    };

    const timer = setTimeout(runSequence, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  const pipelineStages = [
    { label: "New Lead", color: "bg-content-muted" },
    { label: "Qualified", color: "bg-coral-500" },
    { label: "Viewing", color: "bg-info" },
    { label: "Closed", color: "bg-ok" },
  ];

  return (
    <Section className="px-6 py-28 max-w-7xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          See your crew in action
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-content">
          Watch your agents collaborate in real time.
        </h2>
        <p className="mt-3 text-content-muted max-w-lg mx-auto text-base">
          A real deal, handled end-to-end. No scripts. No rules engine.
          Just AI agents qualifying, scheduling, and following up — like
          your best team would, at 10:47 PM on a Thursday.
        </p>
      </motion.div>

      <div ref={sectionRef} className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left sidebar — live deal stats */}
        <motion.div
          variants={scaleIn}
          className="lg:col-span-1 space-y-3"
        >
          {/* Deal card */}
          <div className="bg-surface-secondary/50 border border-surface-border rounded-xl p-4">
            <p className="text-[10px] text-content-muted uppercase tracking-wider mb-3">
              Active Request
            </p>
            <p className="text-sm font-semibold text-content">
              Marina Tower 2BR
            </p>
            <p className="text-xs text-content-muted mt-0.5">Ahmed Al-Maktoum</p>
            <div className="mt-3 space-y-2">
              <div className="flex justify-between text-[11px]">
                <span className="text-content-muted">Value</span>
                <span className="text-content font-medium">{dealValue}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-content-muted">Response</span>
                <span className="text-ok font-medium">
                  {responseTime}
                </span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-content-muted">Agents</span>
                <div className="flex -space-x-1.5">
                  {agents.slice(0, 3).map((a, i) => (
                    <img
                      key={i}
                      src={a.avatar}
                      alt={a.name}
                      className="w-4 h-4 rounded-full border border-surface object-cover"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline progress */}
          <div className="bg-surface-secondary/50 border border-surface-border rounded-xl p-4">
            <p className="text-[10px] text-content-muted uppercase tracking-wider mb-3">
              Pipeline Stage
            </p>
            <div className="space-y-2">
              {pipelineStages.map((stage, i) => (
                <div key={i} className="flex items-center gap-2">
                  <motion.div
                    className={`w-2.5 h-2.5 rounded-full border-2 ${
                      i <= pipelineStage
                        ? `${stage.color} border-transparent`
                        : "bg-transparent border-surface-hover"
                    }`}
                    animate={
                      i === pipelineStage
                        ? {
                            boxShadow: [
                              "0 0 0 0 rgba(255,255,255,0)",
                              "0 0 0 4px rgba(255,255,255,0.1)",
                              "0 0 0 0 rgba(255,255,255,0)",
                            ],
                          }
                        : {}
                    }
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <span
                    className={`text-[11px] ${
                      i <= pipelineStage
                        ? "text-content font-medium"
                        : "text-content-muted"
                    }`}
                  >
                    {stage.label}
                  </span>
                  {i === pipelineStage && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="ml-auto text-[9px] text-coral-400 bg-coral-500/10 rounded-full px-1.5 py-0.5"
                    >
                      Current
                    </motion.span>
                  )}
                </div>
              ))}
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-coral-500 to-identity-blue rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${((pipelineStage + 1) / 4) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
          </div>

          {/* Performance stats */}
          <div className="bg-surface-secondary/50 border border-surface-border rounded-xl p-4">
            <p className="text-[10px] text-content-muted uppercase tracking-wider mb-3">
              Today&apos;s Performance
            </p>
            <div className="space-y-3">
              <div>
                <p className="text-2xl font-bold text-coral-400">
                  <AnimatedNumber target={47} suffix="" />
                </p>
                <p className="text-[10px] text-content-muted">Requests handled</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-ok">
                  <AnimatedNumber target={12} suffix="s" />
                </p>
                <p className="text-[10px] text-content-muted">Avg. response</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-info">
                  <AnimatedNumber target={100} suffix="%" />
                </p>
                <p className="text-[10px] text-content-muted">Follow-up rate</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main chat area */}
        <motion.div
          variants={scaleIn}
          custom={1}
          className="lg:col-span-3 rounded-2xl overflow-hidden border border-surface-border bg-surface shadow-2xl shadow-black/40"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-surface-border bg-surface-secondary/80">
            <div className="flex items-center gap-2">
              <Hash size={14} className="text-coral-400" />
              <span className="text-sm text-content font-medium">
                marina-tower-deal
              </span>
              <span className="ml-1 bg-coral-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {visibleMessages}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {agents.slice(0, 3).map((a, i) => (
                  <img
                    key={i}
                    src={a.avatar}
                    alt={a.name}
                    className="w-6 h-6 rounded-full border-2 border-surface object-cover"
                  />
                ))}
              </div>
              <span className="flex items-center gap-1 text-[10px] text-ok">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ok opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-ok" />
                </span>
                Live
              </span>
            </div>
          </div>

          {/* Pinned context */}
          <div className="px-5 py-2.5 border-b border-surface-border/50 bg-surface-secondary/30">
            <div className="flex items-center gap-2 text-[11px] text-content-muted">
              <Pin size={11} className="text-content-muted rotate-45" />
              <span>
                Ahmed Al-Maktoum — Marina Tower 2BR — AED 1.8M budget —{" "}
                <motion.span
                  key={pipelineStage}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-info"
                >
                  {pipelineStages[pipelineStage].label}
                </motion.span>
              </span>
            </div>
          </div>

          {/* Message thread */}
          <div
            ref={threadRef}
            className="px-5 py-4 space-y-1 h-[480px] overflow-y-auto scroll-smooth"
          >
            {messages.slice(0, visibleMessages).map((msg, i) =>
              msg.isSystem ? (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-2 py-2 px-2"
                >
                  <div className="w-px h-4 bg-surface-border" />
                  {msg.systemIcon && (
                    <motion.div
                      initial={{ rotate: -90, scale: 0 }}
                      animate={{ rotate: 0, scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <msg.systemIcon
                        size={13}
                        className={msg.systemColor || "text-content-muted"}
                      />
                    </motion.div>
                  )}
                  <span className="text-[12px]">{msg.content}</span>
                  <span className="text-[10px] text-content-muted/50 ml-auto shrink-0">
                    {msg.time}
                  </span>
                </motion.div>
              ) : (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex gap-3 py-2.5 px-2 rounded-lg hover:bg-surface-secondary/50 transition-colors"
                >
                  <motion.img
                    src={msg.agent.avatar}
                    alt={msg.agent.name}
                    className="w-9 h-9 rounded-lg object-cover shrink-0 mt-0.5"
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13px] font-semibold text-content">
                        {msg.agent.name}
                      </span>
                      <span className="text-[10px] text-content-muted">
                        {msg.agent.role}
                      </span>
                      <span className="text-[10px] text-content-muted/50">
                        {msg.time}
                      </span>
                    </div>
                    <div className="mt-1 text-[13px] text-content-secondary leading-relaxed">
                      {msg.content}
                    </div>
                    {msg.reactions && (
                      <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex gap-1.5 mt-2"
                      >
                        {msg.reactions.map((r, ri) => (
                          <motion.span
                            key={ri}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              delay: 0.6 + ri * 0.15,
                              type: "spring",
                              stiffness: 400,
                            }}
                            className="inline-flex items-center gap-1 bg-surface-tertiary/80 border border-surface-hover/40 rounded-full px-2 py-0.5 text-[11px]"
                          >
                            {r.emoji}{" "}
                            <span className="text-content-muted">{r.count}</span>
                          </motion.span>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )
            )}

            {typingAgent && <TypingIndicator agent={typingAgent} />}
          </div>

          {/* Input bar */}
          <div className="px-5 py-3 border-t border-surface-border bg-surface-secondary/30">
            <div className="flex items-center gap-3 bg-surface-tertiary/50 rounded-xl px-4 py-3 border border-surface-hover/40">
              <AtSign size={16} className="text-content-muted shrink-0" />
              <span className="text-[13px] text-content-muted flex-1">
                {typingAgent
                  ? `${typingAgent.name} is typing...`
                  : "Agents are handling this autonomously..."}
              </span>
              <Paperclip size={16} className="text-content-muted" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom annotation cards */}
      <motion.div
        variants={fadeUp}
        custom={1}
        className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        {[
          {
            icon: Zap,
            title: "8 seconds to action",
            copy: "Sara qualified Ahmed, matched 3 listings, and tagged Omar for viewing prep — before Ahmed closed the app.",
            color: "text-coral-400",
          },
          {
            icon: Users,
            title: "Zero-touch handoff",
            copy: "Omar prepped docs, booked viewings, and updated the pipeline — unprompted.",
            color: "text-info",
          },
          {
            icon: Clock,
            title: "Automatic follow-up",
            copy: "Layla queued the next touchpoint before anyone could forget.",
            color: "text-ok",
          },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={i}
              variants={fadeUp}
              custom={i + 2}
              className="flex items-start gap-3 bg-surface-secondary/30 border border-surface-border/40 rounded-xl p-4"
            >
              <Icon size={18} className={item.color + " shrink-0 mt-0.5"} />
              <div>
                <p className="text-sm font-medium text-content">
                  {item.title}
                </p>
                <p className="text-xs text-content-muted mt-0.5">{item.copy}</p>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </Section>
  );
}
